/* eslint-disable react-native/no-inline-styles */
/**
 * OrderCard Component
 * 
 * Displays order information in a card format with:
 * - Order ID, status, and timestamp
 * - Total amount and payment method
 * - Restaurant/canteen name
 * - Print functionality for order receipts
 * - Navigation to order details page
 * 
 * @component
 * @param {Object} order - Order object with all order details
 * @param {number} [borderRadius=4] - Border radius for the card
 * @param {boolean} [isOrderDetails] - Whether shown on order details page
 * @param {boolean} [isOrderReviewModal] - Whether shown in review modal
 */
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Card, Divider, Text } from 'react-native-paper';
import moment from 'moment';
import { useNavigation } from '@react-navigation/native';
import { doGet, doPut } from '../services/http';
import { hideLoader, showLoader, showToast } from '../store/info.slice';
import { useDispatch, useSelector } from 'react-redux';
import * as BluetoothPrintService from '../services/BluetoothPrintService';
import { printOrderData } from '../services/PrintService';
import OrderItemsModal from './OrderItemsModal';

function OrderCard({ order, borderRadius = 4, isOrderDetails, isOrderReviewModal }) {
    const dispatch = useDispatch();
    const navigation = useNavigation();
    // Local state for order data that may be updated after print
    // Customer settings determine print availability
    const [tempOrder, setOrder] = useState(order);
    const customer = useSelector(state => state?.customer?.customer);
    const [isDuplicatePrint] = useState(true);
    const enable_print = ([100, 200, 400, 500].includes(order?.order_status)) && customer?.customer_settings?.enable_order_print && customer?.customer_settings?.enable_order_duplicate_print;
    const disableOrderDetailsNavigation = isOrderDetails || isOrderReviewModal;
    const [visible, setVisible] = useState(false);
    /**
     * Closes the order items modal
     */
    const onItemsModalClose = () => {
        setVisible(false);
    };

    /**
     * Gets card styling based on context
     * @returns {Object} Style object for Card component
     */
    const getCardRadius = () => {
        let style = {
            backgroundColor: '#ffffff',
            margin: 2,
            borderRadius,
        };
        if (isOrderReviewModal) {
            return {
                ...style,
                borderRadius,
            };
        }
        return style;
    };

    /**
     * Navigates to order details screen
     * Disabled when already on details page or in review modal
     */
    const gotoOrderDetails = () => {
        if (!disableOrderDetailsNavigation) {
            navigation.push('OrderDetails', { order_id: tempOrder?.order_id });
        }
    };

    /**
     * Handles order receipt printing
     * - Checks print limit per order
     * - Fetches print data if not cached
     * - Prints via Bluetooth (native) or HTTP (web)
     * - Updates print count on success
     */
    const onPrintHandler = async () => {
        try {
            if (Number(tempOrder?.total_order_prints) >= Number(tempOrder?.max_prints_per_order)) {
                dispatch(showToast('Maximum print limit reached for this order.'));
                return;
            }
            let printData = tempOrder?.print_data;

            if (!printData) {
                dispatch(showLoader());
                const response = await doGet('/orders/print', { order_id: order?.order_id });
                dispatch(hideLoader());
                printData = response.data?.print_data;
                if (!printData) {
                    return;
                } else {
                    setOrder({
                        ...tempOrder,
                        print_data: printData,
                    });
                }
            }

            const payload = { ...printData, isDuplicatePrint };
            let isPrintSuccess = false;
            if (Platform.OS === 'web') {
                isPrintSuccess = await printOrderData(payload);
            } else {
                isPrintSuccess = await BluetoothPrintService?.printOrderData(payload);
            }
            if (isPrintSuccess) {
                const updateResponse = await doPut('orders/print', { order_id: order?.order_id });
                if (updateResponse?.data?.status) {
                    setOrder({
                        ...tempOrder,
                        total_order_prints: Number(updateResponse.data.total_order_prints),
                    });
                }
            }

        } catch (error) {
            console.error(error);
            dispatch(hideLoader());
        }
    };

    return (
        <React.Fragment>
            <Card elevation={0} style={[getCardRadius(), { minHeight: 152, flex: 1 }]} onPress={gotoOrderDetails}>
                <Card.Content>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 }}>
                        <View>
                            <Text variant="titleMedium">
                                OrderId: #{tempOrder?.order_code}
                            </Text>
                        </View>
                        <Text variant="titleSmall" style={{ fontSize: 14 }}>
                            <React.Fragment>
                                {tempOrder?.currency_symbol}{tempOrder?.grand_total}
                            </React.Fragment>
                        </Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text variant="titleSmall" style={{ fontSize: 14, textTransform: 'capitalize' }}>
                            {tempOrder?.canteen_name}
                        </Text>
                        <Text style={[styles.orderStatus, { backgroundColor: tempOrder?.os_color || '#000000' }]}>{tempOrder?.order_status_name}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8 }}>
                        <View>
                            <Text style={{ fontSize: 12 }}>
                                Created on {moment(tempOrder?.created_at).format('MMMM D, YYYY')}<Text style={{ color: '#666666', fontSize: 12 }}> at {moment(tempOrder?.created_at).format('h:mm a')}</Text>
                            </Text>
                        </View>
                    </View>
                </Card.Content>
                <Divider style={{ backgroundColor: '#dddddd' }} />
                <Card.Content style={{ paddingVertical: 4 }}>
                    {!!isOrderDetails && (
                        <React.Fragment>
                            {tempOrder.order_items.map(orderItem => {
                                return (
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 }}>
                                        <Text style={{ fontSize: 12, color: '#666666' }}>
                                            {orderItem?.item_name}
                                        </Text>
                                        <Text style={{ fontSize: 12, color: '#666666' }}>
                                            {tempOrder?.item_price_shown ?
                                                <React.Fragment>{orderItem?.item_qty} X {tempOrder?.currency_symbol}{orderItem?.item_price}</React.Fragment> : (
                                                    <React.Fragment>{orderItem?.item_qty}</React.Fragment>
                                                )
                                            }
                                        </Text>
                                    </View>
                                );
                            })}
                        </React.Fragment>
                    )}
                    {!isOrderDetails && tempOrder?.order_items_info && (
                        <Text variant="titleSmall" style={{ fontSize: 12 }}>
                            Items: {tempOrder?.order_items_info}
                        </Text>
                    )}
                    {tempOrder?.cancel_reason && <Text style={{ fontSize: 12 }}>Cancel Reason: {tempOrder?.cancel_reason}</Text>}
                </Card.Content>
                <React.Fragment>
                    <View style={{ flexDirection: 'row', marginHorizontal: 8, marginVertical: 2, gap: 16 }}>
                        {enable_print && <Button icon={'printer-outline'} onPress={onPrintHandler} compact={true} mode="outlined" style={styles.btnStyle} labelStyle={styles.labelStyle}>Print</Button>}
                    </View>
                </React.Fragment>
            </Card>
            <OrderItemsModal visible={visible} onClose={onItemsModalClose} tempOrder={tempOrder} />
        </React.Fragment>
    );
}

const styles = StyleSheet.create({
    orderStatus: {
        fontSize: 10,
        textTransform: 'capitalize',
        color: '#ffffff',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    modalBorderRadius: {
        borderRadius: 4,
    },
    btnStyle: {
        borderRadius: 4,
        paddingVertical: 0,
        marginVertical: 0,
    },
    labelStyle: {
        fontSize: 12,
        paddingVertical: 0,
        textTransform: 'capitalize',
        marginVertical: 6,
    },
});

export default OrderCard;
